import { RSAA } from 'redux-api-middleware';
import type { RSAACallRequest } from '@makemydeal/dr-react-18-wrappers';
import { ApiRequestStatus } from '@makemydeal/dr-shared-types';
import type { Selectors } from './selectors';
import { getDealPdfFetchStatus } from './selectors';
import { FETCH_DEAL_SUMMARY_PDF_REQUEST, FETCH_DEAL_SUMMARY_PDF_SUCCESS, FETCH_DEAL_SUMMARY_PDF_FAILURE } from './actionTypes';

type DealPdfActionTypes = { request: string; success: string; failure: string };

/**
 * `dealPdfFetchStatus` guards against a redundant request being dispatched while one is
 * already in flight.
 */
const dispatchDealPdfFetch =
    (selectors: Selectors, actionTypes: DealPdfActionTypes) => async (dispatch: any, getState: () => any) => {
        const state = getState();

        if (getDealPdfFetchStatus(state) === ApiRequestStatus.Loading) {
            return;
        }

        const endpoint = selectors.additionalSelectors.getDealPdfEndpoint(state);
        const dealXgVersion = selectors.additionalSelectors.getDealXgVersion(state);
        // Stamped as meta so the reducer can record which version `dealPdfUrl` reflects
        const meta = { dealXgVersion };
        // AMD-originated deals carry their complete deal data, plus dealer identity/address/branding
        // (so the generated PDF's dealer block isn't blank), so the BFF can map a proposal PDF without
        // a separate fetch (F302128 Part A, Story 1); non-AMD deals send no payload, unchanged.
        const isAmdOriginated = selectors.additionalSelectors.isAmdOriginated(state);
        const body = isAmdOriginated
            ? {
                  dealData: selectors.additionalSelectors.getActiveDeal(state),
                  dealerInfo: selectors.additionalSelectors.getDealerInfo(state)
              }
            : {};
        const request: RSAACallRequest = {
            endpoint,
            method: 'POST',
            body: JSON.stringify(body),
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            // CloudFront can serve the SPA's own index.html (200, text/html) for this route right after a
            // version bump; without this check RSAA treats that 200 as success and the reducer crashes
            // reading `.data` off an unparsed payload.
            ok: (res) => res.ok && (res.headers.get('content-type') ?? '').includes('json'),
            types: [actionTypes.request, { type: actionTypes.success, meta }, actionTypes.failure],
            fetch: async (...args: Parameters<typeof fetch>) => await fetch(...args)
        };
        await dispatch({ [RSAA]: request });
    };

/**
 * Fetches the deal-summary PDF URL via RSAA. The BFF endpoint is provided by the host app
 * through additionalSelectors.getDealPdfEndpoint, so this activity stays app-agnostic.
 */
export const getDealPdf = (selectors: Selectors) =>
    dispatchDealPdfFetch(selectors, {
        request: FETCH_DEAL_SUMMARY_PDF_REQUEST,
        success: FETCH_DEAL_SUMMARY_PDF_SUCCESS,
        failure: FETCH_DEAL_SUMMARY_PDF_FAILURE
    });
